import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useApp } from "../context/AppContext";

/* ── helpers ── */
function fmtDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("ar-SD", { year: "numeric", month: "short", day: "numeric" });
}

function hexTint(hex, alpha) {
  const h = (hex || "#888").replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/* ── Carousel card (international sources, horizontal strip) ── */
function CarouselCard({ item }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const hasImg = item.image && !imgFailed;

  const handleClick = (e) => {
    if (expanded) return;
    e.preventDefault();
    setExpanded(true);
  };
  const handleExpandedClick = (e) => {
    e.stopPropagation();
    window.open(item.link, "_blank", "noreferrer");
  };

  return (
    <div
      className={`nc-card${expanded ? " expanded" : ""}${!hasImg ? " nc-no-img" : ""}`}
      style={{ "--src-color": item.color || "#555", "--src-tint": hexTint(item.color, 0.13) }}
      onClick={expanded ? undefined : handleClick}
    >
      {hasImg && (
        <img className="nc-card-img" src={item.image} alt={item.title}
          onError={() => setImgFailed(true)} />
      )}
      <div className="nc-card-body">
        <div className="nc-card-source-row">
          {item.icon && <img className="nc-card-favicon" src={item.icon} alt="" />}
          <span className="nc-card-source">{item.source}</span>
        </div>
        <p className="nc-card-title">{item.title}</p>
        {item.summary && <p className="nc-card-summary">{item.summary.slice(0, 280)}{item.summary.length > 280 ? "…" : ""}</p>}
        {item.date && <span className="nc-card-date">{fmtDate(item.date)}</span>}
        {expanded && (
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button className="btn btn-sm" onClick={handleExpandedClick}>
              <i className="fa-solid fa-arrow-up-right-from-square" /> Open
            </button>
            <button className="btn btn-outline btn-sm" onClick={(e) => { e.stopPropagation(); setExpanded(false); }}>
              ✕
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Section card (Dabanga / Ayin, full grid) ── */
function SectionCard({ item, accentColor }) {
  const [imgFailed, setImgFailed] = useState(false);
  const hasImg = item.image && !imgFailed;
  return (
    <a className={`ns-card${!hasImg ? " ns-no-img" : ""}`} href={item.link} target="_blank" rel="noreferrer"
       style={{ "--accent": accentColor, "--accent-tint": hexTint(accentColor, 0.13) }}>
      {hasImg && (
        <div className="ns-card-img">
          <img src={item.image} alt={item.title} onError={() => setImgFailed(true)} />
        </div>
      )}
      <div className="ns-card-body">
        {item.date && <div className="ns-card-date">{fmtDate(item.date)}</div>}
        <div className="ns-card-title">{item.title}</div>
        {item.summary && (
          <p className="ns-card-summary">
            {item.summary.slice(0, 160)}{item.summary.length > 160 ? "…" : ""}
          </p>
        )}
        <div className="ns-card-src-row">
          {item.icon && <img className="ns-card-favicon" src={item.icon} alt="" />}
          <span className="ns-card-src" style={{ color: accentColor }}>{item.source}</span>
        </div>
      </div>
    </a>
  );
}

/* ── Section loader hook (server proxy) ── */
function useNewsSection(feedKey) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("loading");
  const load = useCallback(() => {
    setStatus("loading");
    axios.get(`/api/news?feed=${feedKey}`)
      .then(r => { setItems(r.data.items || []); setStatus("ready"); })
      .catch(() => setStatus("error"));
  }, [feedKey]);
  useEffect(() => { load(); }, [load]);
  return { items, status, reload: load };
}


/* ── NewsSection component ── */
function NewsSection({ title, feedKey, accentColor, dir = "rtl", extraControls }) {
  const { items, status, reload } = useNewsSection(feedKey);
  const { t } = useApp();
  return (
    <div className="ns-section">
      <div className="ns-section-header" style={{ borderColor: accentColor }}>
        <h2 className="ns-section-title" style={{ color: accentColor }}>{title}</h2>
        {extraControls}
      </div>
      {status === "loading" && (
        <div className="empty-state">
          <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: "1.6rem", color: accentColor, marginBottom: 10 }} />
          <p>{t("loading")}</p>
        </div>
      )}
      {status === "error" && (
        <div className="empty-state">
          <p style={{ marginBottom: 10 }}>{t("newsError") || "Could not load feed."}</p>
          <button className="btn btn-outline" onClick={reload}>
            <i className="fa-solid fa-rotate-right" /> {t("retry") || "Retry"}
          </button>
        </div>
      )}
      {status === "ready" && (
        <div className="ns-grid" dir={dir}>
          {items.map((item, i) => <SectionCard key={i} item={item} accentColor={accentColor} />)}
        </div>
      )}
    </div>
  );
}

/* ── Main News page ── */
export default function News() {
  const { t } = useApp();
  const scrollRef = useRef(null);
  const [carouselItems, setCarouselItems] = useState([]);
  const [carouselStatus, setCarouselStatus] = useState("loading");
  const [dabangaLang, setDabangaLang] = useState("ar");

  useEffect(() => {
    axios.get("/api/news/carousel")
      .then(r => { setCarouselItems(r.data.items || []); setCarouselStatus("ready"); })
      .catch(() => setCarouselStatus("error"));
  }, []);

  const pausedRef = useRef(false);
  const autoScrollRef = useRef(null);

  const startAutoScroll = useCallback(() => {
    if (autoScrollRef.current) return;
    autoScrollRef.current = setInterval(() => {
      if (pausedRef.current || !scrollRef.current) return;
      const el = scrollRef.current;
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 4) {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        el.scrollBy({ left: 300, behavior: "smooth" });
      }
    }, 3500);
  }, []);

  const stopAutoScroll = useCallback(() => {
    clearInterval(autoScrollRef.current);
    autoScrollRef.current = null;
  }, []);

  useEffect(() => {
    if (carouselStatus === "ready" && carouselItems.length > 0) startAutoScroll();
    return () => stopAutoScroll();
  }, [carouselStatus, carouselItems.length, startAutoScroll, stopAutoScroll]);

  const scroll = (dir) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  return (
    <>
      <div className="page-band">
        <div className="container">
          <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 6 }}>
            <img src="/Logoithad.jpg" alt="SPAU" style={{ height: 52, borderRadius: 8, opacity: 0.92 }} />
            <div>
              <h1 style={{ marginBottom: 2 }}>{t("newsTitle")}</h1>
              <p style={{ margin: 0 }}>{t("newsSub")}</p>
            </div>
          </div>
        </div>
      </div>

      <section className="section container">

        {/* ── International carousel ── */}
        <div className="nc-wrap">
          <div className="nc-header">
            <h2 className="nc-heading">{t("newsIntl") || "أخبار عالمية"}</h2>
            <div className="nc-arrows">
              <button className="nc-arrow" onClick={() => scroll(1)}>
                <i className="fa-solid fa-chevron-left" />
              </button>
              <button className="nc-arrow" onClick={() => scroll(-1)}>
                <i className="fa-solid fa-chevron-right" />
              </button>
            </div>
          </div>

          {carouselStatus === "loading" && (
            <div className="nc-strip">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="nc-card nc-card-skeleton" />
              ))}
            </div>
          )}
          {carouselStatus === "error" && (
            <p className="muted" style={{ padding: "16px 0" }}>Could not load international feed.</p>
          )}
          {carouselStatus === "ready" && (
            <div className="nc-strip" ref={scrollRef}
              onMouseEnter={() => { pausedRef.current = true; }}
              onMouseLeave={() => { pausedRef.current = false; }}
              onTouchStart={() => { pausedRef.current = true; }}
              onTouchEnd={() => { setTimeout(() => { pausedRef.current = false; }, 2000); }}
            >
              {carouselItems.map((item, i) => <CarouselCard key={i} item={item} />)}
            </div>
          )}
        </div>

        {/* ── Dabanga section ── */}
        <NewsSection
          title="دبنقا"
          feedKey={`dabanga_${dabangaLang}`}
          accentColor="#c0392b"
          dir={dabangaLang === "ar" ? "rtl" : "ltr"}
          extraControls={
            <div className="ns-lang-toggle">
              <button className={dabangaLang === "ar" ? "active" : ""} onClick={() => setDabangaLang("ar")}>عربي</button>
              <button className={dabangaLang === "en" ? "active" : ""} onClick={() => setDabangaLang("en")}>English</button>
            </div>
          }
        />

        {/* ── Ayin section ── */}
        <NewsSection
          title="شبكة عين"
          feedKey="ayin"
          accentColor="#27ae60"
          dir="rtl"
        />


      </section>
    </>
  );
}
